import { PAYMENTS_ENABLED, DEVELOPMENT_ALERT_PREFERENCES, MARKETPLACE_PREFERENCES_ENABLED, CONTACT_ASSURANCE_ENABLED } from '../lib/environment';
import { usersApiUrl } from '../lib/serviceUrls';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { SwissButton, SwissNotice, SwissPanel, SwissSectionHeader } from './swiss';
import {
    AccountAlertFields,
    DEFAULT_ALERT_SETTINGS,
    DEFAULT_MARKETPLACE_SETTINGS,
    EMAIL_ALERT_ERROR,
    PHONE_ERROR,
    PHONE_SMS_ERROR,
    editableAlertSettings,
    settingsSaveError,
    isValidEmail,
    isValidPhone,
    normalisePhoneInput,
} from './account/AlertSettingsForm';

import { ContactVerificationPanel } from './account/ContactVerificationPanel';

// Read lazily so the environment flag is evaluated at render time rather than import time.
const initialSettings = () => ({
    ...DEFAULT_ALERT_SETTINGS,
    ...((DEVELOPMENT_ALERT_PREFERENCES || MARKETPLACE_PREFERENCES_ENABLED) ? DEFAULT_MARKETPLACE_SETTINGS : {}),
});

// `prefill` ({ source, threshold }) comes from a deal's "Alert me to deals like this":
// it sets that source's threshold as an unsaved edit and opens its settings. `today`
// holds today's deals for each source's threshold context (useTodayDeals).
export const EmailAlerts = ({ membershipActive, metadata: suppliedMetadata, onSaved, prefill, today = null } = {}) => {
    const { user, isLoaded, isSignedIn } = useUser();
    const { getToken } = useAuth();
    const marketplacePreferences = DEVELOPMENT_ALERT_PREFERENCES || MARKETPLACE_PREFERENCES_ENABLED;
    const [formData, setFormData] = useState(initialSettings);
    const editedFields = useRef(new Set());
    const initializedUser = useRef(null);
    const prefilledUser = useRef(null);
    // Unsaved edits show the save bar; Undo reloads the saved settings (resetCount).
    const [dirty, setDirty] = useState(false);
    const [resetCount, setResetCount] = useState(0);
    const mounted = useRef(true);
    const currentUserId = useRef(user?.id);
    currentUserId.current = user?.id;
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    const [isSaving, setIsSaving] = useState(false);
    const [submitMessage, setSubmitMessage] = useState(null);
    const [phoneError, setPhoneError] = useState('');
    const [contactStatus, setContactStatus] = useState(null);

    const isSubscribed = membershipActive === undefined
        ? user?.publicMetadata?.subscriptionStatus === 'active'
        : membershipActive === true;

    useEffect(() => {
        if (isLoaded && isSignedIn && user) {
            if (initializedUser.current !== user.id) {
                editedFields.current.clear();
                initializedUser.current = user.id;
                setDirty(false);
                setSubmitMessage(null);
                setPhoneError('');
                setIsSaving(false);
            }
            const metadata = suppliedMetadata === undefined ? user.publicMetadata || {} : suppliedMetadata || {};
            const settings = {
                email: metadata.email?.trim() ? metadata.email : user.primaryEmailAddress?.emailAddress || '',
                phone: metadata.phone || '',
                wine_alerts: metadata.wine_alerts || false,
                sms_alerts: metadata.sms_alerts || false,
                wine_threshold: parseFloat(metadata.wine_threshold || DEFAULT_ALERT_SETTINGS.wine_threshold).toFixed(1),
                same_wine_day_interval: String(metadata.same_wine_day_interval || DEFAULT_ALERT_SETTINGS.same_wine_day_interval),
                ...(marketplacePreferences ? {
                    marketplace_email_alerts: metadata.marketplace_email_alerts === true,
                    marketplace_sms_alerts: metadata.marketplace_sms_alerts === true,
                    marketplace_threshold: Number(metadata.marketplace_threshold || DEFAULT_MARKETPLACE_SETTINGS.marketplace_threshold).toFixed(1),
                    marketplace_repeat_interval: String(metadata.marketplace_repeat_interval || DEFAULT_MARKETPLACE_SETTINGS.marketplace_repeat_interval),
                } : {}),
            };
            const loaded = Object.fromEntries(Object.entries(settings).filter(([key]) => !editedFields.current.has(key)));
            // The deal's threshold is applied once, as if typed, so it saves like any edit.
            const prefillKey = prefill && (prefill.source === 'wine' || marketplacePreferences) ? `${prefill.source}_threshold` : null;
            if (prefillKey && prefilledUser.current !== user.id && !editedFields.current.has(prefillKey)) {
                prefilledUser.current = user.id;
                editedFields.current.add(prefillKey);
                loaded[prefillKey] = prefill.threshold;
                setDirty(true);
            }
            setFormData(previous => ({ ...previous, ...loaded }));
        }
    }, [isLoaded, isSignedIn, user, suppliedMetadata, marketplacePreferences, prefill, resetCount]);

    const undoChanges = () => {
        editedFields.current.clear();
        setPhoneError('');
        setSubmitMessage(null);
        setDirty(false);
        setResetCount(count => count + 1);
    };

    const handleInputChange = (name, value) => {
        editedFields.current.add(name);
        setDirty(true);
        setSubmitMessage(null);
        if (name === 'phone') {
            const phoneValid = isValidPhone(value);
            if (!phoneValid) {
                if (formData.sms_alerts) editedFields.current.add('sms_alerts');
                if (marketplacePreferences && formData.marketplace_sms_alerts) editedFields.current.add('marketplace_sms_alerts');
            }
            setFormData(prevData => ({
                ...prevData,
                phone: value,
                sms_alerts: prevData.sms_alerts && phoneValid,
                ...(marketplacePreferences ? { marketplace_sms_alerts: prevData.marketplace_sms_alerts && phoneValid } : {}),
            }));
            setPhoneError(value.length > 0 && !phoneValid ? PHONE_ERROR : '');
            return;
        }
        setFormData(prevData => ({ ...prevData, [name]: value }));
    };

    const handleToggleChange = (name, checked) => {
        editedFields.current.add(name);
        setSubmitMessage(null);
        if ((name === 'wine_alerts' || name === 'marketplace_email_alerts') && checked && !isValidEmail(formData.email)) {
            setSubmitMessage({ type: 'error', text: EMAIL_ALERT_ERROR });
            return;
        }
        if ((name === 'sms_alerts' || name === 'marketplace_sms_alerts') && checked && !isValidPhone(formData.phone)) {
            setPhoneError(PHONE_SMS_ERROR);
            return;
        }
        setDirty(true);
        setFormData(prevData => ({ ...prevData, [name]: checked }));
    };

    const handleChannelsChange = changes => {
        Object.keys(changes).forEach(key => editedFields.current.add(key));
        setDirty(true);
        setSubmitMessage(null);
        setFormData(previous => ({ ...previous, ...changes }));
    };

    const handleSaveSettings = async (e) => {
        e.preventDefault();
        if ((formData.sms_alerts || formData.marketplace_sms_alerts) && !isValidPhone(formData.phone)) {
            setPhoneError(PHONE_SMS_ERROR);
            return;
        }
        setIsSaving(true);
        setSubmitMessage(null);
        let saved = false;
        const savingUserId = user?.id;
        const isCurrentUser = () => mounted.current && currentUserId.current === savingUserId;
        try {
            const token = await getToken();
            if (!isCurrentUser()) return;
            const response = await fetch(usersApiUrl('/update-metadata'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    metadata: editableAlertSettings(marketplacePreferences
                        ? Object.fromEntries(Object.entries(formData).filter(([key]) => editedFields.current.has(key)))
                        : formData, marketplacePreferences)
                }),
            });
            if (!isCurrentUser()) return;
            if (response.ok) {
                saved = true;
                if (onSaved) await onSaved();
                if (!isCurrentUser()) return;
                setDirty(false);
                setSubmitMessage({ type: 'success', text: 'Alert settings saved.' });
            } else {
                const message = await settingsSaveError(response, 'Failed to update settings. Please try again.');
                if (isCurrentUser()) setSubmitMessage({ type: 'error', text: message });
            }
        } catch (error) {
            if (!isCurrentUser()) return;
            console.error('Error updating settings:', error);
            setSubmitMessage({ type: 'error', text: saved
                ? 'Settings were saved, but account details could not be refreshed. Please retry to refresh your settings.'
                : 'Failed to update settings. Please try again.' });
        } finally {
            if (isCurrentUser()) setIsSaving(false);
        }
    };

    const handleSubscribe = () => { window.location.assign('/setup-account'); };

    if (!isLoaded || !isSignedIn) {
        return (
            <SwissPanel>
                <div className="ff-loading" role="status">
                    <span className="ff-loading__spinner" aria-hidden="true" />
                    <span>Loading alert settings…</span>
                </div>
            </SwissPanel>
        );
    }

    if (!isSubscribed) {
        return (
            <SwissPanel className="ff-alert-panel">
                <SwissSectionHeader title="Alerts" />
                <SwissNotice className="mb-4 mt-4">
                    Email and SMS deal alerts are available to Wine Tracker Plus members. Subscribe to turn them on.
                </SwissNotice>
                <SwissButton
                    onClick={handleSubscribe}
                    disabled={!PAYMENTS_ENABLED}
                >
                    Subscribe now
                </SwissButton>
            </SwissPanel>
        );
    }

    const savedContacts = suppliedMetadata === undefined ? user.publicMetadata || {} : suppliedMetadata || {};
    const savedEmail = savedContacts.email?.trim() || user.primaryEmailAddress?.emailAddress || '';
    const currentContactStatus = contactStatus && {
        ...contactStatus,
        suppressions: {
            email: formData.email.trim().toLowerCase() === savedEmail.trim().toLowerCase() && contactStatus.suppressions?.email === true,
            sms: normalisePhoneInput(formData.phone) === normalisePhoneInput(savedContacts.phone) && contactStatus.suppressions?.sms === true,
        },
    };

    return (
        <SwissPanel className="ff-alert-panel">
            <SwissSectionHeader title="Alerts" subtitle="We’ll email or text you when a deal beats your price." />
            <form onSubmit={handleSaveSettings} className="ff-alert-form" noValidate>
                {prefill && (prefill.source === 'wine' || marketplacePreferences) && (
                    <p className="ff-notice ff-alert-prefill" role="status">
                        {prefill.source === 'wine'
                            ? `Wine threshold set to ${prefill.threshold}¢ from the deal you picked. Wine alerts compare the price with the bonus points alone, so this can differ from the tracker’s figure.`
                            : `Marketplace threshold set to ${prefill.threshold}¢ from the deal you picked.`}
                        {' '}Choose email or SMS if they’re off, then save to keep it.
                    </p>
                )}
                <AccountAlertFields
                    key={resetCount}
                    today={today}
                    initiallyOpen={prefill && (prefill.source === 'wine' || marketplacePreferences) ? prefill.source : null}
                    formData={formData}
                    onInputChange={handleInputChange}
                    onToggle={handleToggleChange}
                    onChannelsChange={handleChannelsChange}
                    phoneError={phoneError}
                    marketplace={marketplacePreferences}
                    contactStatus={currentContactStatus}
                    mobileVerification={CONTACT_ASSURANCE_ENABLED && <ContactVerificationPanel
                        key={`contact-${user?.id}-${savedEmail.trim().toLowerCase()}-${suppliedMetadata?.wine_alerts}-${suppliedMetadata?.sms_alerts}-${suppliedMetadata?.marketplace_email_alerts}-${suppliedMetadata?.marketplace_sms_alerts}`}
                        compact metadata={suppliedMetadata === undefined ? user.publicMetadata : suppliedMetadata}
                        draftPhone={formData.phone} onStateChange={setContactStatus}
                    />}
                />

                {submitMessage && (
                    <div
                        role={submitMessage.type === 'error' ? 'alert' : 'status'}
                        className={`ff-notice ${submitMessage.type === 'success' ? 'ff-notice--success' : 'ff-notice--error'}`}
                    >
                        {submitMessage.text}
                    </div>
                )}
                <div className={`ff-alert-form__actions ff-alert-savebar${dirty ? ' is-dirty' : ''}`}>
                    {dirty && <p className="ff-alert-savebar__text">You have unsaved changes</p>}
                    {dirty && <SwissButton type="button" variant="ghost" onClick={undoChanges} disabled={isSaving}>Undo changes</SwissButton>}
                    <SwissButton type="submit" disabled={isSaving}>
                        {isSaving ? 'Saving…' : submitMessage?.type === 'error' ? 'Retry saving settings' : 'Save settings'}
                    </SwissButton>
                </div>
            </form>
        </SwissPanel>
    );
};
